// Import React tools
import React, {useContext, useEffect, useRef, useState, } from "react";

// Import strich api calls
import{
    requestStripeBillingLink,
    completeStripeBillingLink,
    requestStripeBillingUnlink,
    getUserAccountData,
} from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Import button component
import CreateButton from "global/components/CreateButton";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";

// Import styles
import "./StripeBillingManager.css";
import { formatDisplayDateTime } from "global/utilities/ConvertDate";




// Export the Stripe script loader hook
export const useStripeJsLoader = () => {

    // Track whether the Stripe script has loaded
    const [isStripeLoaded, setIsStripeLoaded] = useState(false);

    // Track whether the Stripe script failed to load
    const [stripeLoadError, setStripeLoadError] = useState("");

    // Load the Stripe script once
    useEffect(() => {

        // If Stripe is already available on the window then mark it as loaded
        if (window.Stripe) {

            // Set the script as loaded
            setIsStripeLoaded(true);

            // Stop here because we do not need to inject the script again
            return;

        }

        // Try to find an existing Stripe script tag
        const existingScript = document.getElementById("stripe-js-script");

        // If a matching script already exists then hook into its load lifecycle
        if (existingScript) {

            // Create a handler for a successful load
            const handleLoad = () => {

                // Mark Stripe as loaded
                setIsStripeLoaded(true);

                // Clear any previous load error
                setStripeLoadError("");

            };

            // Create a handler for a failed load
            const handleError = () => {

                // Save a helpful error message
                setStripeLoadError("Failed to load Stripe.js");

            };

            // Listen for load events
            existingScript.addEventListener("load", handleLoad);

            // Listen for error events
            existingScript.addEventListener("error", handleError);

            // If Stripe became available before the listeners attached then update now
            if (window.Stripe) {

                // Mark Stripe as loaded
                setIsStripeLoaded(true);

                // Clear any previous load error
                setStripeLoadError("");

            }

            // Clean up the listeners when the component unmounts
            return () => {

                // Remove the load listener
                existingScript.removeEventListener("load", handleLoad);

                // Remove the error listener
                existingScript.removeEventListener("error", handleError);

            };

        }

        // Create a new script tag for Stripe.js
        const script = document.createElement("script");

        // Give the script a stable id so it can be reused
        script.id = "stripe-js-script";

        // Set the Stripe hosted script url
        script.src = "https://js.stripe.com/v3/";

        // Load asynchronously
        script.async = true;

        // When the script loads successfully
        script.onload = () => {

            // Mark Stripe as loaded
            setIsStripeLoaded(true);

            // Clear any previous load error
            setStripeLoadError("");

        };

        // When the script fails to load
        script.onerror = () => {

            // Save a helpful error message
            setStripeLoadError("Failed to load Stripe.js");

        };

        // Append the script to the document head
        document.head.appendChild(script);

    }, []);

    // Return the loader state
    return {
        isStripeLoaded,
        stripeLoadError,
    };

};





// Export the Stripe billing manager component
export const StripeBillingManager = ({ userData, setUserData }) => {

    // Get the auth token
    const { token } = useContext(AuthContext);

    // Define toast options
    const { toastSuccess, toastError } = useToast();

    // Load Stripe.js
    const {
        isStripeLoaded,
        stripeLoadError,
    } = useStripeJsLoader();

    // Store the current Stripe billing status label
    const [statusText, setStatusText] = useState(userData?.stripe_billing_link_date ? "Linked" : "Not linked");

    // Store the general UI message
    const [message, setMessage] = useState("");

    // Track whether the component is loading status data
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Track whether a link session is being created
    const [isStartingLink, setIsStartingLink] = useState(false);

    // Track whether the setup is being confirmed
    const [isConfirmingLink, setIsConfirmingLink] = useState(false);

    // Track whether unlink is in progress
    const [isRemovingLink, setIsRemovingLink] = useState(false);

    // Store the client secret for visibility or debugging if needed later
    const [clientSecret, setClientSecret] = useState("");

    // Store the in-progress setup intent id separately from the linked one
    const [pendingSetupIntentId, setPendingSetupIntentId] = useState("");

    // Store local account data so the UI can update after link and unlink
    const [accountData, setAccountData] = useState(userData || {});

    // Create a ref for the Stripe payment element mount point
    const paymentElementContainerRef = useRef(null);

    // Create a ref for the Stripe instance
    const stripeInstanceRef = useRef(null);

    // Create a ref for the Stripe elements instance
    const elementsInstanceRef = useRef(null);

    // Create a ref for the mounted payment element instance
    const paymentElementInstanceRef = useRef(null);



    // Sync local account data when parent userData changes
    useEffect(() => {

        // Update the local account data from props
        setAccountData(userData || {});

        // Update the status text from props
        if (userData?.stripe_billing_link_date) {

            // Mark linked when a link date exists
            setStatusText("Linked");

        } else {

            // Otherwise mark as not linked
            setStatusText("Not linked");

        }

    }, [userData]);



    // This safely destroys the current Stripe payment element once
    const destroyPaymentElement = () => {

        // Read the current payment element instance
        const currentPaymentElement = paymentElementInstanceRef.current;

        // If there is no current payment element then stop
        if (!currentPaymentElement) {

            // Stop here
            return;

        }

        try {

            // Destroy the Stripe payment element
            currentPaymentElement.destroy();

        } catch (error) {

            // Log a warning rather than crashing when Stripe says it was already destroyed
            //console.warn("Stripe payment element destroy skipped:", error);

        } finally {

            // Clear the payment element ref immediately so it cannot be destroyed twice
            paymentElementInstanceRef.current = null;

            // Clear the payment element mount container when it exists
            if (paymentElementContainerRef.current) {

                // Remove any leftover dom content from the mount point
                paymentElementContainerRef.current.innerHTML = "";

            }

        }

    };



    // Create a helper to fully reset the temporary Stripe setup UI
    const resetStripeBillingUi = () => {

        // Clear the message
        setMessage("");

        // Clear the client secret
        setClientSecret("");

        // Clear the pending setup intent id
        setPendingSetupIntentId("");

        // Safely destroy the mounted Stripe payment element
        destroyPaymentElement();

        // Clear the Stripe elements ref
        elementsInstanceRef.current = null;

        // Clear the Stripe instance ref
        stripeInstanceRef.current = null;

    };



    // Clean up Stripe elements when the component unmounts
    useEffect(() => {

        // Return the unmount cleanup
        return () => {

            // Safely destroy the mounted payment element
            destroyPaymentElement();

            // Clear the Stripe elements ref
            elementsInstanceRef.current = null;

            // Clear the Stripe instance ref
            stripeInstanceRef.current = null;

        };

    }, []);



    // Refresh the user account data from the backend
    const refreshAccountData = async () => {

        // Set the refresh loading state
        setIsRefreshing(true);

        try {

            // Request the latest account data
            const getUserDataResponse = await getUserAccountData(token, setUserData);

            // If the response succeeded and returned results
            if (getUserDataResponse?.success && getUserDataResponse?.results) {

                // Update the status based on the latest data
                if (getUserDataResponse.results?.stripe_billing_link_date) {

                    // Mark as linked
                    setStatusText("Linked");

                } else {

                    // Mark as not linked
                    setStatusText("Not linked");

                }

            }

        } catch (error) {

            // Log the refresh error for debugging
            console.error(error);

        } finally {

            // Clear the refresh loading state
            setIsRefreshing(false);

        }

    };



    // Start the Stripe billing link process
    const startStripeBillingLink = async () => {

        // Stop if Stripe.js is not available yet
        if (!isStripeLoaded || !window.Stripe) {

            // Clear the message
            setMessage(null);

            // Toast error message
            toastError({ title: "Error", message: "Stripe.js is not ready yet: " + stripeLoadError, autoCloseMs: 6000 });

            // Stop here
            return;

        }

        // Reset the temporary billing UI
        resetStripeBillingUi();

        // Set the link loading state
        setIsStartingLink(true);
       
        try {

            // Call the create endpoint
            const response = await requestStripeBillingLink(token);

            // If the backend failed
            if (!response?.success) {

                // Clear the message
                setMessage(null);

                // Toast error message
                toastError({ title: "Error", message: "Failed to create Stripe billing link: " + response?.error_message, autoCloseMs: 6000 });

                // Stop here
                return;

            }

            // Validate the required Stripe payload
            if (!response?.results?.publishable_key || !response?.results?.client_secret || !response?.results?.setup_intent_id) {

                // Clear the message
                setMessage(null);

                // Toast error message
                toastError({ title: "Error", message: "Stripe billing link did not return the required details.", autoCloseMs: 6000 });

                // Stop here
                return;

            }

            // Show progress text
            setMessage("Creating Stripe SetupIntent...");

            // Create the Stripe instance
            const nextStripeInstance = window.Stripe(response.results.publishable_key);

            // Create the Stripe elements instance
            const nextElementsInstance = nextStripeInstance.elements({
                clientSecret: response.results.client_secret,
            });

            // Create the payment element
            const nextPaymentElementInstance = nextElementsInstance.create("payment");

            // If the payment mount container exists
            if (paymentElementContainerRef.current) {

                // Mount the payment element into the container
                nextPaymentElementInstance.mount(paymentElementContainerRef.current);

            }

            // Save the Stripe instance to the ref
            stripeInstanceRef.current = nextStripeInstance;

            // Save the Stripe elements instance to the ref
            elementsInstanceRef.current = nextElementsInstance;

            // Save the payment element instance to the ref
            paymentElementInstanceRef.current = nextPaymentElementInstance;

            // Save the client secret
            setClientSecret(response.results.client_secret);

            // Save the pending setup intent id
            setPendingSetupIntentId(response.results.setup_intent_id);

            // Update the status to show this is in progress
            setStatusText("In progress");

            // Show the next step to the user
            setMessage("Enter card details and confirm the Stripe billing link");

        } catch (error) {

            // Log the error for debugging
            console.error(error);

            // Show a failure message
            setMessage("Failed to create Stripe billing link");

        } finally {

            // Clear the link loading state
            setIsStartingLink(false);

        }

    };



    // Confirm the Stripe billing link after the card details are entered
    const confirmStripeBillingLink = async () => {

        // Stop if the Stripe session is not ready
        if (!stripeInstanceRef.current || !elementsInstanceRef.current) {

            // Show a helpful message
            setMessage("Stripe billing setup is not ready");

            // Stop here
            return;

        }

        // Set the confirm loading state
        setIsConfirmingLink(true);

        // Show progress text
        setMessage("Confirming Stripe setup...");

        try {

            // Confirm the setup with Stripe
            const stripeResult =
                await stripeInstanceRef.current.confirmSetup({
                    elements: elementsInstanceRef.current,
                    redirect: "if_required",
                });

            // If Stripe returned a validation or payment error
            if (stripeResult?.error) {

                // Show the Stripe validation message without closing the form
                setMessage(
                    stripeResult.error.message ||
                    "Please complete all required card details."
                );

                // Stop here and allow the user to correct the details
                return;

            }

            // Default to the locally stored setup intent id
            let completedSetupIntentId = pendingSetupIntentId;

            // If Stripe returned a setup intent id then use that
            if (stripeResult?.setupIntent?.id) {

                // Save the returned setup intent id
                completedSetupIntentId =
                    stripeResult.setupIntent.id;

            }

            // If there is still no setup intent id then fail gracefully
            if (!completedSetupIntentId) {

                // Show the error without closing the form
                setMessage(
                    "Stripe did not return a setup intent id after confirmation"
                );

                // Stop here
                return;

            }

            // Show progress text
            setMessage("Finalising Stripe billing link...");

            // Call the completion endpoint
            const completeResponse =
                await completeStripeBillingLink(
                    token,
                    encodeURIComponent(completedSetupIntentId)
                );

            // If the backend failed the final step
            if (!completeResponse?.success) {

                // Build the failure message
                let completeErrorMessage =
                    "Failed to complete Stripe billing link";

                // Include the API error when one was returned
                if (completeResponse?.error_message) {

                    completeErrorMessage +=
                        ": " + completeResponse.error_message;

                }

                // Keep the form visible and show the failure
                setMessage(completeErrorMessage);

                // Also display the failure as a toast
                toastError({
                    title: "Error",
                    message: completeErrorMessage,
                    autoCloseMs: 6000,
                });

                // Stop here
                return;

            }

            // Refresh the account data after a successful link
            await refreshAccountData();

            // Clear the temporary Stripe setup only after success
            resetStripeBillingUi();

            // Toast success
            toastSuccess({
                title: "Success",
                message: "Stripe billing link completed.",
            });

        } catch (error) {

            // Start with a safe version of the error
            let errorMessage = String(error);

            // Prefer the normal Error message when available
            if (error instanceof Error && error.message) {

                errorMessage = error.message;

            }

            // Build the displayed failure
            const displayedError =
                "Failed to complete Stripe billing link: " +
                errorMessage;

            // Keep the form visible and show the failure
            setMessage(displayedError);

            // Toast error message
            toastError({
                title: "Error",
                message: displayedError,
                autoCloseMs: 6000,
            });

        } finally {

            // Only clear the loading state here
            setIsConfirmingLink(false);

        }

    };


    // Cancel the current Stripe billing link flow
    const cancelStripeBillingLink = () => {

        // Ensure confirming state is false
        setIsConfirmingLink(false);

        // Clear the temporary Stripe UI
        resetStripeBillingUi();

        // Reset the status text based on the saved account data
        if (accountData?.stripe_billing_link_date) {

            // Mark as linked
            setStatusText("Linked");

        } else {

            // Mark as not linked
            setStatusText("Not linked");

        }

    };



    const handleRemoveBillingLink = () => {

        // Give dialog - are you sure you want to log out?
        DisplayConfirmationDialog(

            // Dialog message
            "<h3>Are you sure you want to unlink Stripe?</h3><br/>Billing for fulfilments and charges will no longer be able to be taken automatically from this account.",

            // Confirm button title
            "Yes",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            function (is_confirmed) {

                // Check if the response is TRUE
                if (is_confirmed) {

                    // It is, so remove stripe billing link
                    removeStripeBillingLink();
                    
                }
            }
        );

    }


    // Remove the Stripe billing link
    const removeStripeBillingLink = async () => {
       
        // Reset the ui
        resetStripeBillingUi();

        // Set the remove loading state
        setIsRemovingLink(true);

        try {

            // Call the remove endpoint
            const response = await requestStripeBillingUnlink(token);

            // If the backend reported failure
            if (!response?.success) {

                // Clear the message
                setMessage(null);

                // Toast error message
                toastError({ title: "Error", message: "Failed to remove Stripe billing link: " + response?.error_message, autoCloseMs: 6000 });

                // Stop here
                return;

            }

            // Refresh the account data after unlinking
            await refreshAccountData();

            // Clear the message
            setMessage(null);

            // Toast success 
            toastSuccess({ title: "Success", message: "Stripe billing link removed." });

        } catch (error) {

            // Clear the message
            setMessage(null);

            // Toast error message
            toastError({ title: "Error", message: "Failed to remove Stripe billing link: " + error, autoCloseMs: 6000 });

        } finally {

            // Clear the remove loading state
            setIsRemovingLink(false);

        }

    };



    // Work out whether any action is currently running
    const isAnyActionLoading = isRefreshing || isStartingLink || isConfirmingLink || isRemovingLink;



    // Build a safe expiry display value
    const expiryDisplay =
        accountData?.stripe_billing_exp_month && accountData?.stripe_billing_exp_year
            ? `${String(accountData.stripe_billing_exp_month).padStart(2, "0")} / ${accountData.stripe_billing_exp_year}`
            : "-";



    // Work out whether Stripe is currently linked
    const isStripeLinked = !!accountData?.stripe_billing_link_date;



    // Return the component UI
    return (

        <div className="billing-method-container">
       
            <section>

                {/* This is the heading area */}
                <div className="stripe-billing-manager__header">

                    <div className={`horizontal-align-content-center account-billing-preference__header ${accountData?.stripe_billing_link_date ? "account-billing-preference__header--active" : "account-billing-preference__header--inactive"}`}><h3>Stripe</h3></div>

                    {/* This is the status sub heading */}
                    <p className="stripe-billing-manager__status">
                        <span className={`stripe-billing-manager__status-badge stripe-billing-manager__status-badge--${accountData?.stripe_billing_link_date ? "linked" : "unlinked"}`}>
                            {statusText}
                        </span>
                    </p>

                </div>

                {accountData?.stripe_billing_link_date ?

                    <div>
                        <span>Stripe linked to allow automatic billing for fulfilments and charges.</span>

                        <br/>

                        {accountData?.stripe_billing_link_date &&

                            <span className="stripe-billing-manager__status-date">
                                Linked Since: {formatDisplayDateTime(accountData?.stripe_billing_link_date, false)}
                            </span>

                        }

                    </div>

                    :

                    <p>Link Stripe to allow automatic billing for fulfilments and charges.</p>

                }

                <br/>
                

                {/* If there is a linked date then show the user stripe data */}
                {accountData?.stripe_billing_link_date &&

                    <div className="stripe-billing-manager__details">

                        {/* This is the card brand row */}
                        <p className="stripe-billing-manager__detail">
                            <strong>Card Type:</strong> {accountData?.stripe_billing_brand || "-"}
                        <br/>

                        {/* This is the last 4 row */}
                        
                            <strong>Card Number:</strong> {accountData?.stripe_billing_last4 ? `**** **** **** ${accountData?.stripe_billing_last4}` : "-"}
                        <br/>

                        {/* This is the expiry row */}
                        
                            <strong>Expiry:</strong> {expiryDisplay}
                        </p>
                        
                        <br/>

                    </div>

                }

                

                {/* This is the button row */}
                <div className="stripe-billing-manager__actions">

                    {/* Check if the account is linked */}
                    {isStripeLinked ?
                    
                        // This removes the Stripe billing link
                        <CreateButton
                            title={isRemovingLink ? "Unlinking..." : "Unlink Stripe"}
                            size="s"
                            type="button"
                            invert={true}
                            onClick={handleRemoveBillingLink}
                            disabled={isAnyActionLoading}
                        />

                    :

                        // If there's currently a message then something is happening so we don't need show a button
                        message ? 

                            ""

                            :

                        // This starts the Stripe billing link flow
                        <CreateButton
                            title={isStartingLink ? "Starting..." : "Link Stripe"}
                            size="s"
                            type="button"
                            onClick={startStripeBillingLink}
                            disabled={isAnyActionLoading || !isStripeLoaded}
                        />

                    }

                </div>

                {/* This shows Stripe script load errors */}
                {stripeLoadError &&

                    <div className="stripe-billing-manager__message stripe-billing-manager__message--error">
                        {stripeLoadError}
                    </div>

                }

                {/* This shows general flow messages */}
                {message &&

                    <div className="stripe-billing-manager__message">
                        {message}
                    </div>

                }

                {/* This holds the Stripe payment element */}
                <div className="stripe-billing-manager__element-shell">

                    {/* This is the Stripe payment element mount point */}
                    <div
                        ref={paymentElementContainerRef}
                        className="stripe-billing-manager__element-container"
                    />

                </div>

                {/* Only show the confirm row when a Stripe setup session exists */}
                {!!clientSecret &&

                    <div className="stripe-billing-manager__confirm-row">

                        {/* This confirms the billing link */}
                        <CreateButton
                            title={isConfirmingLink ? "Confirming..." : "Confirm Stripe billing link"}
                            size="s"
                            type="button"
                            onClick={confirmStripeBillingLink}
                            disabled={isAnyActionLoading}
                        />

                        {/* This cancels the current setup flow */}
                        <CreateButton
                            title="Cancel"
                            isCancel={true}
                            size="s"
                            invert={true}
                            type="button"
                            onClick={cancelStripeBillingLink}
                            disabled={isAnyActionLoading}
                        />

                    </div>

                }

            </section>

        </div>

    );

};

// Export the component as default as well for flexibility
export default StripeBillingManager;